import React, { useEffect, useState } from 'react'

import dayjs from 'dayjs'
import { collection, doc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import { IoAddOutline } from 'react-icons/io5'

import LastLog from './lists/LastLog'
import LogsLists from './lists/LogsLists'
import Log from './Log/Log'
import Accordion from './ObjetivesSection'
import { Button, DeleteLogModal } from '../../../..'
import {
  useProfessors,
  useStorage,
  useStudents,
  useUser,
} from '../../../../../context'
import { firestore } from '../../../../../firebase/firebaseConfig'
import { prepareCopiedLog } from '../../../../../services/logCopy'
import { NewLog } from '../../../../../types'
import { Spacer } from '../../../../global'

const _items = [
  {
    title: 'Objetivos del alumno',
    content: [],
  },
]

const INITIAL_STATE = {
  date: dayjs().format('YYYY-MM-DD'),
  objetives: null,
  exercises: [],
  songTitle: null,
}

const LogSection = () => {
  const { user, setOnEditionMode } = useUser()
  const { studentLogs, deleteLog, activeStudent, uploadLog, setOnLogCreation } =
    useStudents()
  const { professorsList, activeLog } = useProfessors()

  const { getAudioList, setProgessBar, deleteAudiosFromLog } = useStorage()
  const [log, setLog] = useState<NewLog | null>(null)
  const [deleteMessage, setDeleteMessage] = useState<{
    error: boolean | null
    message: string
    hasButton?: boolean
  } | null>(null)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [deleteData, setDeleteData] = useState<{
    studentUID: string
    logUID: string
    professorUID: string
  } | null>(null)

  const handleAddLog = async () => {
    if (!activeStudent || !user?.uid) return
    const newLog = createLog()
    if (newLog) {
      setLog({
        ...INITIAL_STATE,
        professorUID: user.uid,
        ...newLog,
      })
    }

    setOnEditionMode(true)
  }

  const createLog = () => {
    if (!activeStudent) return
    setProgessBar(0)
    const studentRef = doc(firestore, 'students', activeStudent.uid)

    const logsCollectionRef = collection(studentRef, 'logs')
    const logDocRef = doc(logsCollectionRef)
    const now = dayjs().format('YYYY-MM-DD')

    setOnLogCreation(true)
    return { date: now, uid: logDocRef.id }
  }

  const handleCopyLog = async (logItem: NewLog) => {
    if (!activeStudent || !user?.uid) return
    const newLog = createLog()

    if (!newLog?.uid || !newLog.date) return

    const updatedLogItem = prepareCopiedLog(logItem, {
      date: newLog.date,
      professorUID: user.uid,
      uid: newLog.uid,
    })

    const response = await uploadLog(
      activeStudent.uid,
      updatedLogItem,
      activeStudent.activeBonus,
    )

    if (response.error) {
      setOnLogCreation(false)
      toast.error(response.message)
      return
    }

    setLog(updatedLogItem)
    setOnEditionMode(true)
  }

  const handleDelete = (
    studentUID: string,
    logUID: string,
    professorUID: string,
  ) => {
    setDeleteData({ studentUID, logUID, professorUID })
    setOpenModal(true)
    setDeleteMessage({
      error: null,
      message: 'Seguro que quieres borrar esta bitácora?',
      hasButton: true,
    })
  }

  const handleDeleteLog = async () => {
    if (deleteData) {
      try {
        await toast.promise(
          (async () => {
            await deleteAudiosFromLog(deleteData.studentUID, deleteData.logUID)
            const response = await deleteLog(
              deleteData.studentUID,
              deleteData.logUID,
              deleteData.professorUID,
            )

            if (response?.error) {
              throw new Error(response.message)
            }

            return response
          })(),
          {
            loading: 'Eliminando bitácora...',
            success: (res) =>
              res?.message
                ? `${res.message}`
                : 'Bitácora eliminada correctamente',
            error: (err) =>
              typeof err?.message === 'string'
                ? `${err.message}`
                : 'Error al eliminar la bitácora',
          },
        )
        setDeleteData(null)
        setOpenModal(false)
      } catch (error: unknown) {
        setDeleteMessage({
          error: true,
          message:
            error instanceof Error
              ? error.message
              : 'Error al eliminar la bitácora',
          hasButton: false,
        })
      }
    }
  }

  const handleClose = () => {
    setDeleteMessage(null)
    setOpenModal(false)
  }

  useEffect(() => {
    getAudioList()
    setLog(activeLog)
  }, [activeStudent, activeLog, getAudioList])

  useEffect(() => {
    setOnEditionMode(false)
  }, [])

  return (
    <>
      {deleteMessage && openModal && (
        <DeleteLogModal
          handleClose={handleClose}
          message={deleteMessage.message}
          handleDelete={handleDeleteLog}
          hasButton={deleteMessage.hasButton}
        />
      )}

      <Accordion items={_items} type="primary" />
      <Spacer height={16} />
      {log !== null ? (
        <Log logData={log} setLogData={setLog} />
      ) : (
        <>
          {user?.uid && (
            <Button
              onClick={handleAddLog}
              width="142px"
              fontSize={14}
              className="dark-grey"
              label={
                <>
                  <IoAddOutline />
                  BITÁCORA
                </>
              }
            />
          )}
          <Spacer height={30} />
          <LastLog
            setLog={setLog}
            handleCopyLog={handleCopyLog}
            handleDelete={handleDelete}
          />
          <Spacer height={30} />
          {studentLogs && professorsList && (
            <LogsLists
              setLog={setLog}
              handleCopyLog={handleCopyLog}
              logList={studentLogs}
              personLists={professorsList}
              handleDelete={handleDelete}
            />
          )}
        </>
      )}
    </>
  )
}

export default LogSection
